import { useState } from "react";
import { Pencil, UserX } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const NONE = "__none";

const NickEdit = ({ s, send, onDone }) => {
  const [v, setV] = useState(s.nickname || "");
  const save = (nickname) => { send({ type: "cmd", action: "set_nickname", student_id: s.id, nickname }); onDone(); };
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(v); }} className="flex items-center gap-2 pl-5 pb-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} maxLength={16} className="h-8 text-sm" data-testid={`nick-input-${s.id}`} autoFocus />
      <button type="submit" className="text-xs text-[#34D399] font-bold" data-testid={`nick-save-${s.id}`}>Kaydet</button>
      <button type="button" onClick={() => save("")} className="text-xs text-amber-400" data-testid={`nick-reset-${s.id}`}>Yeniden sor</button>
    </form>
  );
};

export const StudentsTable = ({ state, send }) => {
  const [kick, setKick] = useState(null);
  const [editing, setEditing] = useState(null);
  const nick = state.mode === "guess";
  const lobby = state.phase === "lobby";
  const students = [...state.students].sort((a, b) => a.first_name.localeCompare(b.first_name, "tr"));
  return (
    <div className="panel p-4 space-y-3" data-testid="students-table">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-xl font-bold">Öğrenciler</h3>
        <span className="text-sm text-slate-400">{state.connected_count}/{state.student_count} bağlı</span>
      </div>
      {students.length === 0 && <p className="text-slate-400 text-sm">Henüz katılan yok.</p>}
      {students.some((s) => s.connected && s.away) && (
        <div className="rounded-xl border border-[#FACC15]/50 bg-[#FACC15]/10 px-3 py-2 text-sm text-[#FDE68A]" data-testid="teacher-away">
          <b>Uygulamadan çıkan ({students.filter((s) => s.connected && s.away).length}):</b> {students.filter((s) => s.connected && s.away).map((s) => `${s.first_name} ${s.last_name}`).join(", ")}
        </div>
      )}
      {students.some((s) => !s.connected) && (
        <div className="rounded-xl border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="teacher-offline">
          <b>Bağlantısı kopan ({students.filter((s) => !s.connected).length}):</b> {students.filter((s) => !s.connected).map((s) => `${s.first_name} ${s.last_name}`).join(", ")}
        </div>
      )}
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
        {students.map((s) => (
          <div key={s.id}>
          <div className="flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-150 px-3 py-2" data-testid={`student-row-${s.id}`}>
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${s.connected ? "bg-[#22C55E]" : "bg-red-500 anim-pulse"}`} title={s.connected ? "Bağlı" : "Bağlantı koptu"} />
            <span className={`flex-1 min-w-0 truncate font-semibold ${s.connected ? "" : "text-red-300"}`}>{s.first_name} {s.last_name}{!s.connected && <span className="ml-1 text-xs font-normal">(bağlantı koptu)</span>}
              {s.connected && s.away && <span className="ml-1 text-xs font-normal text-[#FACC15]" data-testid={`student-away-${s.id}`}>(uygulamadan çıktı)</span>}
              {s.away_count > 0 && <span className="ml-1 rounded-full bg-[#FACC15]/15 px-1.5 text-[10px] font-semibold text-[#FDE68A]" title="Uygulamadan çıkma sayısı" data-testid={`student-away-count-${s.id}`}>çıkış ×{s.away_count}</span>}
              {nick && <span className="block text-xs font-normal text-[#FDE047]" data-testid={`student-nick-${s.id}`}>{s.nickname || "takma ad bekleniyor"}</span>}
            </span>
            {nick && (
              <button onClick={() => setEditing(editing === s.id ? null : s.id)} className="p-1.5 rounded hover:bg-white/10" data-testid={`nick-edit-${s.id}`} aria-label="Takma adı düzenle">
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {lobby ? (
              <Select value={s.team_id || NONE} onValueChange={(v) => send({ type: "cmd", action: "assign_team", student_id: s.id, team_id: v === NONE ? null : v })}>
                <SelectTrigger className="h-8 w-36 text-xs" data-testid={`assign-team-${s.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Masasız</SelectItem>
                  {state.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs" style={{ color: state.teams.find((t) => t.id === s.team_id)?.color }}>{state.teams.find((t) => t.id === s.team_id)?.name || "—"}</span>
            )}
            <span className="font-mono-x text-sm w-12 text-right">{s.score}</span>
            <button onClick={() => setKick(s)} className="p-1.5 rounded hover:bg-red-900/50 text-red-400" data-testid={`kick-${s.id}`} aria-label="Öğrenciyi at">
              <UserX className="h-4 w-4" />
            </button>
          </div>
          {editing === s.id && <NickEdit s={s} send={send} onDone={() => setEditing(null)} />}
          </div>
        ))}
      </div>
      <ConfirmDialog open={Boolean(kick)} onOpenChange={(o) => !o && setKick(null)} title="Öğrenci atılsın mı?"
        description={`${kick?.first_name} ${kick?.last_name} odadan atılacak ve bu oyundaki tüm verileri silinecek (KVKK).`}
        confirmText="At ve verileri sil" onConfirm={() => { send({ type: "cmd", action: "kick", student_id: kick.id }); setKick(null); }} />
    </div>
  );
};
